import React, { useCallback, useEffect, useState } from "react";
import { Building2, Check, ChevronsUpDown, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { ClientsApi, errorMessage } from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

/**
 * Workspace switcher for accounting firms: their own workspace plus every client they keep
 * books for. Switching swaps the session token for one scoped to that client.
 */
export const ClientSwitcher = () => {
  const { t } = useI18n();
  const { isAccountingFirm, isAdmin, tenant, applySession } = useAuth();
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  const [name, setName] = useState("");
  const [taxId, setTaxId] = useState("");

  const enabled = isAccountingFirm && isAdmin;

  const load = useCallback(() => {
    if (!enabled) return;
    ClientsApi.list()
      .then((d) => setClients(d.clients || []))
      .catch(() => setClients([]));
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  if (!enabled) return null;

  const activate = async (id) => {
    if (id === tenant?.id) return setOpen(false);
    setBusy(id);
    try {
      const res = await ClientsApi.switchTo(id);
      await applySession(res.token);
      setOpen(false);
      toast.success(t("clients.switched"));
    } catch (e) {
      toast.error(errorMessage(e, "Could not switch workspace"));
    } finally {
      setBusy(null);
    }
  };

  const create = async () => {
    if (!name.trim()) return;
    setBusy("new");
    try {
      const res = await ClientsApi.create({ company_name: name.trim(), tax_id: taxId.trim() || null });
      await applySession(res.token);
      setAdding(false);
      setName("");
      setTaxId("");
      toast.success(t("clients.created", { name: res.client?.name }));
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not create the client workspace"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="max-w-[210px] justify-between gap-2"
            data-testid="client-switcher"
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="truncate">{tenant?.name || t("clients.switcher")}</span>
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[min(20rem,calc(100vw-2rem))]">
          <DropdownMenuLabel>{t("clients.switcher")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {clients.map((c) => (
            <DropdownMenuItem
              key={c.id}
              onSelect={(e) => {
                e.preventDefault();
                activate(c.id);
              }}
              className="gap-2"
              data-testid={`client-option-${c.id}`}
            >
              {busy === c.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : c.id === tenant?.id ? (
                <Check className="h-3.5 w-3.5 text-primary" />
              ) : (
                <span className="w-3.5" />
              )}
              <span className="min-w-0 flex-1 truncate">{c.name}</span>
              {c.is_firm ? (
                <Badge variant="secondary" className="shrink-0 text-[10px]">
                  {t("clients.ownWorkspace")}
                </Badge>
              ) : (
                <span className="ll-num shrink-0 text-[11px] text-muted-foreground">
                  {c.receipts}
                </span>
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setOpen(false);
              setAdding(true);
            }}
            className="gap-2"
            data-testid="client-add-button"
          >
            <Plus className="h-3.5 w-3.5" /> {t("clients.addClient")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent data-testid="client-add-dialog">
          <DialogHeader>
            <DialogTitle>{t("clients.addClient")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="client-name">{t("clients.clientName")}</Label>
              <Input
                id="client-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Prijevoznik d.o.o."
                data-testid="client-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="client-oib">{t("clients.clientTaxId")}</Label>
              <Input
                id="client-oib"
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
                placeholder="12345678901"
                data-testid="client-taxid-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">{t("clients.addHint")}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={create} disabled={!name.trim() || busy === "new"} data-testid="client-create-submit">
              {busy === "new" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("clients.createCta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientSwitcher;
